import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { call } from "../../../firebase";
import { scheduleNext } from "../../../srs/fsrs";
import { createTypedAnswerProvider } from "../answerCapture";
import {
  completeStudySession,
  createStudySession,
  getActiveStudySession,
  getCardsByIds,
  getDueCardsAcrossDecksCount,
  getSessionCardsAcrossDecks,
  saveStudySessionProgress,
  getSrsStreak,
  listDecks,
  markDailyStudyActivity,
  updateDeckCardMedia,
} from "../firestore";
import LatexText from "../components/LatexText";
import { uploadGeneratedCardAudioB64, uploadGeneratedCardImageB64 } from "../storage";
import CelebrationOverlay from "../../../components/CelebrationOverlay";
import StreakBanner from "../components/StreakBanner";

const MASTER_DECK_ID = "__master__";
const DEFAULT_TARGET = 1;
const TTS_VOICES = ["alloy", "echo", "fable", "nova", "onyx", "shimmer", "verse"];

function countReviewedDue(reviews, dueSeedSet) {
  if (!dueSeedSet?.size || !Array.isArray(reviews)) return 0;
  const seen = new Set();
  reviews.forEach((review) => {
    const cardId = review?.cardId;
    if (cardId && dueSeedSet.has(cardId)) seen.add(cardId);
  });
  return seen.size;
}

export default function MasterStudyPage() {
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState("");
  const [targetCount, setTargetCount] = useState(DEFAULT_TARGET);
  const [dueCount, setDueCount] = useState(0);
  const [deckNames, setDeckNames] = useState({});

  const [sessionId, setSessionId] = useState("");
  const [sessionQueue, setSessionQueue] = useState([]);
  const [cardsById, setCardsById] = useState({});
  const [cursor, setCursor] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [capturedAnswer, setCapturedAnswer] = useState("");
  const [done, setDone] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState("");
  const [refineSuccess, setRefineSuccess] = useState("");
  const [mediaBusy, setMediaBusy] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [mediaSuccess, setMediaSuccess] = useState("");
  const [audioGenText, setAudioGenText] = useState("");
  const [audioGenInstruction, setAudioGenInstruction] = useState("");
  const [audioGenVoice, setAudioGenVoice] = useState("alloy");
  const [imageGenInstruction, setImageGenInstruction] = useState("");
  const [showAudioGenText, setShowAudioGenText] = useState(false);
  const [streak, setStreak] = useState({ streakCount: 0, lastStudyDate: null });
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [sessionSource, setSessionSource] = useState("due");
  const [reviewedDueCount, setReviewedDueCount] = useState(0);

  const answerInputRef = useRef("");
  const reviewsRef = useRef([]);
  const cardUpdatesRef = useRef({});
  const sessionDueSeedRef = useRef(new Set());
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    answerInputRef.current = typedAnswer;
  }, [typedAnswer]);

  useEffect(() => {
    let cancelled = false;

    async function loadDeckMap() {
      setLoading(true);
      setError("");
      try {
        const [decks, streakData, totalDue, activeSession] = await Promise.all([
          listDecks(),
          getSrsStreak(),
          getDueCardsAcrossDecksCount(),
          getActiveStudySession(MASTER_DECK_ID),
        ]);
        if (!cancelled) {
          const map = {};
          decks.forEach((deck) => {
            map[deck.id] = deck.name || "Untitled Deck";
          });
          setDeckNames(map);
          setStreak(streakData);
          setDueCount(totalDue);
          setTargetCount(totalDue > 0 ? totalDue : DEFAULT_TARGET);

          if (activeSession?.id) {
            const progressData = activeSession.progress || {};
            const restoredQueue = Array.isArray(progressData.sessionQueue) && progressData.sessionQueue.length
              ? progressData.sessionQueue
              : (Array.isArray(activeSession.cardIds) ? activeSession.cardIds : []);
            const restoredCursor = Math.max(0, Number(progressData.cursor || 0));
            const restoredReviews = Array.isArray(progressData.reviews) ? progressData.reviews : [];
            const restoredUpdates = progressData.cardUpdates || {};
            const restoredSource = progressData.sessionSource || "due";
            const restoredDueSeed = Array.isArray(progressData.dueSeedIds)
              ? progressData.dueSeedIds
              : (restoredSource === "due" ? Array.from(new Set(restoredQueue)) : []);

            if (restoredQueue.length) {
              const uniqueIds = Array.from(new Set(restoredQueue));
              const restoredCards = await getCardsByIds(uniqueIds);
              if (cancelled) return;
              const restoredCardsById = restoredCards.reduce((acc, card) => {
                acc[card.id] = card;
                return acc;
              }, {});

              reviewsRef.current = restoredReviews;
              cardUpdatesRef.current = restoredUpdates;
              sessionDueSeedRef.current = new Set(restoredDueSeed);

              const nextCursor = Math.min(restoredCursor, restoredQueue.length);
              setSessionId(activeSession.id);
              setSessionQueue(restoredQueue);
              setCardsById(restoredCardsById);
              setCursor(nextCursor);
              setSessionSource(restoredSource);
              setDone(false);
              setSessionStats(null);
              setReviewedDueCount(countReviewedDue(restoredReviews, sessionDueSeedRef.current));

              if (nextCursor >= restoredQueue.length) {
                await completeStudySession({
                  sessionId: activeSession.id,
                  deckId: MASTER_DECK_ID,
                  cardUpdates: restoredUpdates,
                  reviews: restoredReviews,
                });
                if (cancelled) return;
                const lapses = restoredReviews.filter((r) => r.grade === "again").length;
                setSessionId("");
                setSessionQueue([]);
                setCardsById({});
                setCursor(0);
                setDone(true);
                setSessionStats({ reviewed: restoredReviews.length, lapses });
                sessionDueSeedRef.current = new Set();
                setReviewedDueCount(0);
                setDueCount(await getDueCardsAcrossDecksCount());
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load decks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDeckMap();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentCardId = sessionQueue[cursor];
  const currentCard = currentCardId ? cardsById[currentCardId] : null;

  const progress = useMemo(() => {
    if (!sessionQueue.length) return "0 / 0";
    return `${Math.min(cursor + 1, sessionQueue.length)} / ${sessionQueue.length}`;
  }, [cursor, sessionQueue.length]);
  const effectiveDueCount = useMemo(
    () => Math.max(0, dueCount - reviewedDueCount),
    [dueCount, reviewedDueCount]
  );

  useEffect(() => {
    setRefineInstruction("");
    setRefineError("");
    setRefineSuccess("");
    setMediaError("");
    setMediaSuccess("");
    setAudioGenText(currentCard ? String(currentCard.back || currentCard.front || "") : "");
    setAudioGenInstruction("");
    setAudioGenVoice("alloy");
    setImageGenInstruction("");
    setShowAudioGenText(false);
  }, [currentCardId, currentCard]);

  const startSession = async () => {
    setStarting(true);
    setError("");

    try {
      const sessionPick = await getSessionCardsAcrossDecks(Number(targetCount) || DEFAULT_TARGET);
      const dueCards = sessionPick.cards;
      setSessionSource(sessionPick.source || "due");
      if (!dueCards.length) {
        setSessionQueue([]);
        setCardsById({});
        setDone(true);
        setSessionStats({ reviewed: 0, lapses: 0 });
        return;
      }

      const nextCardsById = dueCards.reduce((acc, card) => {
        acc[card.id] = card;
        return acc;
      }, {});
      const nextQueue = dueCards.map((card) => card.id);
      const dueSeedIds = sessionPick.source === "due" ? Array.from(new Set(nextQueue)) : [];
      const newSessionId = await createStudySession(MASTER_DECK_ID, nextQueue, Number(targetCount) || DEFAULT_TARGET);

      reviewsRef.current = [];
      cardUpdatesRef.current = {};
      sessionDueSeedRef.current = new Set(dueSeedIds);
      setSessionId(newSessionId);
      setCardsById(nextCardsById);
      setSessionQueue(nextQueue);
      setCursor(0);
      setRevealed(false);
      setTypedAnswer("");
      setCapturedAnswer("");
      setDone(false);
      setSessionStats(null);
      setReviewedDueCount(0);
      await saveStudySessionProgress(newSessionId, {
        sessionQueue: nextQueue,
        cursor: 0,
        reviews: [],
        cardUpdates: {},
        sessionSource: sessionPick.source || "due",
        dueSeedIds,
      });
    } catch (err) {
      setError(err.message || "Failed to start master study session.");
    } finally {
      setStarting(false);
    }
  };

  const handleReveal = async () => {
    const getAnswer = createTypedAnswerProvider(() => answerInputRef.current);
    const result = await getAnswer();
    setCapturedAnswer(result.text);
    setRevealed(true);
  };

  const finishSession = async () => {
    if (!sessionId) return;

    setEnding(true);
    setError("");
    try {
      await completeStudySession({
        sessionId,
        deckId: MASTER_DECK_ID,
        cardUpdates: cardUpdatesRef.current,
        reviews: reviewsRef.current,
      });
      const lapses = reviewsRef.current.filter((r) => r.grade === "again").length;
      setSessionId("");
      setSessionQueue([]);
      setCardsById({});
      setCursor(0);
      setRevealed(false);
      setTypedAnswer("");
      setCapturedAnswer("");
      setSessionSource("due");
      sessionDueSeedRef.current = new Set();
      setReviewedDueCount(0);
      setDone(true);
      setSessionStats({ reviewed: reviewsRef.current.length, lapses });
      setDueCount(await getDueCardsAcrossDecksCount());
    } catch (err) {
      setError(err.message || "Failed to complete session.");
    } finally {
      setEnding(false);
    }
  };

  const navigateCursor = (delta) => {
    if (!sessionQueue.length) return;
    const step = delta < 0 ? -1 : 1;
    let idx = cursor;
    let safety = 0;
    while (safety < sessionQueue.length) {
      idx += step;
      if (idx < 0 || idx >= sessionQueue.length) return;
      const nextId = sessionQueue[idx];
      if (nextId && cardsById[nextId]) {
        setCursor(idx);
        setRevealed(false);
        setTypedAnswer("");
        setCapturedAnswer("");
        return;
      }
      safety += 1;
    }
  };

  const handlePrevCard = () => {
    if (cursor <= 0) return;
    navigateCursor(-1);
  };

  const handleNextCard = () => {
    if (cursor >= sessionQueue.length - 1) return;
    navigateCursor(1);
  };

  const handleToggleAudioPlayback = async () => {
    const player = audioPlayerRef.current;
    if (!player) return;
    if (player.paused) {
      try {
        await player.play();
      } catch (_) {
        // user gesture/browser policy may block; ignore
      }
    } else {
      player.pause();
    }
  };

  const handleGrade = async (grade) => {
    if (!currentCard) return;

    const now = new Date();
    const priorSrs = cardUpdatesRef.current[currentCard.id] || currentCard.srs;
    const { nextSrs } = scheduleNext(priorSrs, grade, now, {});

    const nextCardUpdates = {
      ...cardUpdatesRef.current,
      [currentCard.id]: nextSrs,
    };
    cardUpdatesRef.current = nextCardUpdates;
    try {
      const streakUpdate = await markDailyStudyActivity();
      setStreak((prev) => ({
        ...prev,
        streakCount: streakUpdate.streakCount,
        lastStudyDate: streakUpdate.lastStudyDate,
      }));
      if (streakUpdate.keptToday) {
        setShowStreakCelebration(true);
      }
    } catch (_) {
      // non-blocking streak update
    }

    const nextReviews = [
      ...reviewsRef.current,
      {
      cardId: currentCard.id,
      deckId: currentCard.deckId,
      grade,
      reviewedAt: now,
      transcript: capturedAnswer,
      rawAnswer: capturedAnswer,
      correct: null,
    },
    ];
    reviewsRef.current = nextReviews;

    const nextQueue = grade === "again" ? [...sessionQueue, currentCard.id] : sessionQueue;
    const nextCursor = cursor + 1;
    setSessionQueue(nextQueue);
    setReviewedDueCount(countReviewedDue(nextReviews, sessionDueSeedRef.current));
    try {
      await saveStudySessionProgress(sessionId, {
        sessionQueue: nextQueue,
        cursor: nextCursor,
        reviews: nextReviews,
        cardUpdates: nextCardUpdates,
        sessionSource,
        dueSeedIds: Array.from(sessionDueSeedRef.current),
      });
    } catch (_) {
      // non-blocking progress persistence
    }

    setCursor(nextCursor);
    setRevealed(false);
    setTypedAnswer("");
    setCapturedAnswer("");

    if (nextCursor >= sessionQueue.length + (grade === "again" ? 1 : 0)) {
      await finishSession();
    }
  };

  const patchLocalCard = (cardId, patch) => {
    setCardsById((prev) => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        ...patch,
      },
    }));
  };

  const handleRefineCard = async () => {
    if (!currentCard) return;
    if (!refineInstruction.trim()) {
      setRefineError("Enter a refinement instruction first.");
      return;
    }
    setRefining(true);
    setRefineError("");
    setRefineSuccess("");
    try {
      const res = await call("refineSrsCard", {
        cardId: currentCard.id,
        instruction: refineInstruction.trim(),
      });
      const updated = res?.data?.card || res?.data;
      if (!updated?.front || !updated?.back) throw new Error("No updated card returned.");
      patchLocalCard(currentCard.id, {
        front: updated.front,
        back: updated.back,
        tags: updated.tags || [],
        strict: typeof updated.strict === "boolean" ? updated.strict : !!currentCard.strict,
        accepted: updated.accepted || [],
      });
      setRefineSuccess("Card updated.");
    } catch (err) {
      const msg = err?.code === "functions/not-found"
        ? "refineSrsCard is not deployed. Deploy Firebase functions."
        : err.message || "Failed to refine card.";
      setRefineError(msg);
    } finally {
      setRefining(false);
    }
  };

  const handleGenerateMasterAudio = async () => {
    if (!currentCard) return;
    if (!audioGenText.trim()) {
      setMediaError("Audio text is required.");
      return;
    }
    setMediaBusy(true);
    setMediaError("");
    setMediaSuccess("");
    try {
      const res = await call("generateSrsCardAudio", {
        cardId: currentCard.id,
        text: audioGenText.trim(),
        instruction: audioGenInstruction.trim(),
        voice: audioGenVoice.trim() || "alloy",
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated audio returned.");
      const uploaded = await uploadGeneratedCardAudioB64({ deckId: currentCard.deckId, cardId: currentCard.id, b64 });
      await updateDeckCardMedia(currentCard.id, uploaded);
      patchLocalCard(currentCard.id, uploaded);
      setMediaSuccess("Audio attached to this card.");
    } catch (err) {
      setMediaError(err.message || "Failed to generate audio.");
    } finally {
      setMediaBusy(false);
    }
  };

  const handleGenerateMasterImage = async () => {
    if (!currentCard) return;
    setMediaBusy(true);
    setMediaError("");
    setMediaSuccess("");
    try {
      const res = await call("generateSrsCardImage", {
        cardId: currentCard.id,
        instruction: imageGenInstruction.trim(),
      });
      const b64 = res?.data?.b64;
      if (!b64) throw new Error("No generated image returned.");
      const uploaded = await uploadGeneratedCardImageB64({ deckId: currentCard.deckId, cardId: currentCard.id, b64 });
      await updateDeckCardMedia(currentCard.id, uploaded);
      patchLocalCard(currentCard.id, uploaded);
      setMediaSuccess("Image attached to this card.");
    } catch (err) {
      setMediaError(err.message || "Failed to generate image.");
    } finally {
      setMediaBusy(false);
    }
  };

  useEffect(() => {
    if (!sessionId || done || !currentCard) return undefined;

    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      const tag = target.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.getAttribute?.("contenteditable") === "true") return true;
      return false;
    };

    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      const key = event.key;
      if (!revealed && event.code === "Space") {
        event.preventDefault();
        handleReveal();
        return;
      }

      if ((key === "<" || key === ",") && cursor > 0) {
        event.preventDefault();
        handlePrevCard();
        return;
      }
      if ((key === ">" || key === ".") && cursor < sessionQueue.length - 1) {
        event.preventDefault();
        handleNextCard();
        return;
      }

      if (!revealed || ending) return;

      if (key === "j" || key === "J") {
        event.preventDefault();
        handleGrade("again");
      } else if (key === "k" || key === "K") {
        event.preventDefault();
        handleGrade("hard");
      } else if (key === "l" || key === "L") {
        event.preventDefault();
        handleGrade("good");
      } else if (key === ";") {
        event.preventDefault();
        handleGrade("easy");
      } else if (key === "p" || key === "P") {
        event.preventDefault();
        handleToggleAudioPlayback();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, done, currentCard, revealed, cursor, sessionQueue.length, ending, cardsById]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {showStreakCelebration ? (
          <CelebrationOverlay
            message="Streak kept"
            subtext={`${streak.streakCount} day streak`}
            onClose={() => setShowStreakCelebration(false)}
            duration={2600}
          />
        ) : null}

        <Button component={Link} to="/srs" sx={{ alignSelf: "flex-start" }}>
          Back to Decks
        </Button>

        <StreakBanner streakCount={streak.streakCount} lastStudyDate={streak.lastStudyDate} />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? <Typography>Loading decks...</Typography> : null}

        {!loading ? (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight={800}>Master Study</Typography>
              <Typography color="text.secondary">
                Aggregates due cards across all decks into one study session.
              </Typography>
              <Typography color="text.secondary">Due now across decks: {effectiveDueCount}</Typography>
              <Typography color="text.secondary">Progress: {progress}</Typography>
              {sessionId && sessionSource === "upcoming" ? (
                <Alert severity="info">No cards were due right now, so this session is using the next upcoming cards.</Alert>
              ) : null}

              {!sessionId && !done ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                  <TextField
                    label="Target cards"
                    type="number"
                    value={targetCount}
                    onChange={(e) => setTargetCount(e.target.value)}
                    inputProps={{ min: 1, max: 300 }}
                    sx={{ width: 180 }}
                  />
                  <Button variant="contained" onClick={startSession} disabled={starting}>
                    {starting ? "Starting..." : "Start Master Study"}
                  </Button>
                </Stack>
              ) : null}

              {done ? (
                <Alert severity="success">
                  Master session complete. Reviewed {sessionStats?.reviewed ?? 0} cards. Again count: {sessionStats?.lapses ?? 0}.
                </Alert>
              ) : null}

              {sessionId && currentCard && !done ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Deck: {deckNames[currentCard.deckId] || currentCard.deckId || "Unknown"}
                    </Typography>
                    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Front
                    </Typography>
                    <LatexText component="div" text={currentCard.front} sx={{ fontSize: "1.25rem", fontWeight: 700 }} />
                    {currentCard.imageUrl ? (
                      <Box
                        component="img"
                        src={currentCard.imageUrl}
                        alt="card visual"
                        sx={{ mt: 1.5, maxWidth: "100%", maxHeight: 260, borderRadius: 1, border: "1px solid #ddd" }}
                      />
                    ) : null}
                    {currentCard.audioUrl ? (
                      <Box sx={{ mt: 1.5 }}>
                        <audio
                          ref={audioPlayerRef}
                          controls
                          preload="none"
                          src={currentCard.audioUrl}
                          style={{ width: "100%" }}
                        />
                      </Box>
                    ) : null}
                  </Box>

                  {!revealed ? (
                    <Stack spacing={2}>
                      <TextField
                        label="Your answer"
                        value={typedAnswer}
                        onChange={(e) => setTypedAnswer(e.target.value)}
                        multiline
                        minRows={2}
                      />
                      <Button variant="contained" onClick={handleReveal}>Reveal Answer</Button>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button variant="outlined" onClick={handlePrevCard} disabled={cursor <= 0}>Back (&lt;)</Button>
                        <Button
                          variant="outlined"
                          onClick={handleNextCard}
                          disabled={cursor >= sessionQueue.length - 1}
                        >
                          Forward (&gt;)
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#fafafa" }}>
                        <Typography variant="overline" color="text.secondary">Back</Typography>
                        <LatexText component="div" text={currentCard.back} sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Your answer: {capturedAnswer || "(empty)"}
                        </Typography>
                      </Paper>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button color="error" variant="contained" onClick={() => handleGrade("again")} disabled={ending}>Again</Button>
                        <Button color="warning" variant="contained" onClick={() => handleGrade("hard")} disabled={ending}>Hard</Button>
                        <Button color="success" variant="contained" onClick={() => handleGrade("good")} disabled={ending}>Good</Button>
                        <Button color="info" variant="contained" onClick={() => handleGrade("easy")} disabled={ending}>Easy</Button>
                        <Button variant="outlined" onClick={handlePrevCard} disabled={cursor <= 0}>Back (&lt;)</Button>
                        <Button
                          variant="outlined"
                          onClick={handleNextCard}
                          disabled={cursor >= sessionQueue.length - 1}
                        >
                          Forward (&gt;)
                        </Button>
                        <Button
                          variant="outlined"
                          component={Link}
                          to={`/srs/decks/${currentCard.deckId}/cards/${currentCard.id}`}
                        >
                          Open Card Editor
                        </Button>
                      </Stack>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Refine this card with GPT</Typography>
                          <TextField
                            label="Instruction"
                            placeholder="e.g., include the formal definition with LaTeX notation"
                            value={refineInstruction}
                            onChange={(e) => setRefineInstruction(e.target.value)}
                            multiline
                            minRows={2}
                          />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Button variant="outlined" onClick={handleRefineCard} disabled={refining}>
                              {refining ? "Updating..." : "Update Card with GPT"}
                            </Button>
                            {refineSuccess ? <Alert severity="success">{refineSuccess}</Alert> : null}
                          </Stack>
                          {refineError ? <Alert severity="error">{refineError}</Alert> : null}
                        </Stack>
                      </Paper>

                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle2">Card Media Tools</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add GPT audio/image to this card directly from Master Study.
                          </Typography>
                          {mediaError ? <Alert severity="error">{mediaError}</Alert> : null}
                          {mediaSuccess ? <Alert severity="success">{mediaSuccess}</Alert> : null}

                          <Divider />
                          <Typography variant="body2" fontWeight={700}>GPT Audio</Typography>
                          <TextField
                            label="Text to speak"
                            value={audioGenText}
                            onChange={(e) => setAudioGenText(e.target.value)}
                            type={showAudioGenText ? "text" : "password"}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    edge="end"
                                    aria-label={showAudioGenText ? "Hide audio text" : "Show audio text"}
                                    onClick={() => setShowAudioGenText((prev) => !prev)}
                                  >
                                    {showAudioGenText ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <TextField
                              label="Pronunciation/style (optional)"
                              value={audioGenInstruction}
                              onChange={(e) => setAudioGenInstruction(e.target.value)}
                              fullWidth
                            />
                            <TextField
                              label="Voice"
                              value={audioGenVoice}
                              onChange={(e) => setAudioGenVoice(e.target.value)}
                              select
                              sx={{ width: { xs: "100%", sm: 180 } }}
                            >
                              {TTS_VOICES.map((voice) => (
                                <MenuItem key={voice} value={voice}>{voice}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <Button variant="outlined" onClick={handleGenerateMasterAudio} disabled={mediaBusy}>
                            {mediaBusy ? "Generating..." : "Generate GPT Audio"}
                          </Button>

                          <Divider />
                          <Typography variant="body2" fontWeight={700}>GPT Image</Typography>
                          <TextField
                            label="Image instruction (optional)"
                            value={imageGenInstruction}
                            onChange={(e) => setImageGenInstruction(e.target.value)}
                            placeholder="e.g., diagram of the concept"
                          />
                          <Button variant="outlined" onClick={handleGenerateMasterImage} disabled={mediaBusy}>
                            {mediaBusy ? "Generating..." : "Generate GPT Image"}
                          </Button>
                        </Stack>
                      </Paper>
                    </Stack>
                  )}
                </Stack>
              ) : null}

              {sessionId && !currentCard && !done ? (
                <Alert severity="info">
                  This session has no remaining cards loaded. Click finish to finalize saved progress.
                  <Stack direction="row" sx={{ mt: 1 }}>
                    <Button variant="contained" size="small" onClick={finishSession} disabled={ending}>
                      {ending ? "Finishing..." : "Finish Session"}
                    </Button>
                  </Stack>
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Container>
  );
}
